import { useMemo } from 'react'
import { ROLE_ORG, useRoleContext } from '../role.context'
import { useAuthContext } from '../../context/auth.context'

const fullAccess = { accessCreate: true, accessRead: true, accessUpdate: true, accessDelete: true }
const noAccess = { accessCreate: false, accessRead: false, accessUpdate: false, accessDelete: false }

export const useRoleOrg = type => {
  const authContext = useAuthContext()
  const roleContext = useRoleContext()

  return useMemo(
    () => {
      if (authContext.state.isSysRoot) {
        return fullAccess
      }

      if (authContext.state.isSysAdmin) {
        return fullAccess
      }

      return roleContext.orgRoles[type] || noAccess
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authContext.state, roleContext.orgRoles]
  )
}

export const useRoleOrgDashboard = () => useRoleOrg(ROLE_ORG.DASHBOARD)
export const useRoleOrgNode = () => useRoleOrg(ROLE_ORG.NODE)
export const useRoleOrgUser = () => useRoleOrg(ROLE_ORG.USER)
export const useRoleOrgGroup = () => useRoleOrg(ROLE_ORG.GROUP)
export const useRoleOrgGroupUser = () => useRoleOrg(ROLE_ORG.GROUP_USER)
export const useRoleOrgNodeGroup = () => useRoleOrg(ROLE_ORG.NODE_GROUP)
export const useRoleOrgNodeUser = () => useRoleOrg(ROLE_ORG.NODE_USER)
// export const useRoleOrgHeatmap = () => useRoleOrg(ROLE_ORG.HEATMAP)
export const useRoleOrgAnalytic = () => useRoleOrg(ROLE_ORG.ANALYTIC)
export const useRoleOrgAssessmentManage = () => useRoleOrg(ROLE_ORG.ASSESSMENT_MANAGE)
export const useRoleOrgAssessmentReport = () => useRoleOrg(ROLE_ORG.ASSESSMENT_REPORT)
export const useRoleOrgTradeshow = () => useRoleOrg(ROLE_ORG.TRADESHOW)
export const useRoleOrgSelfRegister = () => useRoleOrg(ROLE_ORG.SELF_REGISTER)
export const useRoleOrgDoc = () => useRoleOrg(ROLE_ORG.DOC)
export const useRoleOrgSSO = () => useRoleOrg(ROLE_ORG.SSO)
// The organization's API keys page (Phase D): API_KEY.
export const useRoleOrgApiKey = () => useRoleOrg(ROLE_ORG.API_KEY)

// ORG_ORGANIZATION (delete organization, spec §2.4). Read = the
// Organization page (GET /organization-deletion), delete = schedule,
// update = restore.
//
// Owner ruling 2026-10-07: organization pages show what the person's ACTUAL
// role in the organization allows, even for a super admin or root;
// super-admin powers belong to the staff system pages only. So this reads
// the person's own role memberships here (roleContext.orgRoles, from
// role-permission/:userID/user, which has no super-admin shortcut), never
// useRoleOrg's full access: a super admin who holds the owner role gets the
// owner's page (the server lets a real owner schedule and restore as one),
// one who holds admin the admin's, and one with neither isn't offered the
// page. Only this scope: every other org hook keeps the shortcut (a
// post-release backlog item).
//
// An impersonated session reads as the impersonated person's own role does
// (the token is theirs) and never deletes or restores: the server refuses
// it (403 impersonation, ruling R8). readOnly says why the writes are
// withheld, IMPERSONATED when the person's role could write (an
// impersonated admin sees just what the admin sees); null otherwise.
export const ORGANIZATION_READ_ONLY = { IMPERSONATED: 'impersonated' }

export const useRoleOrgOrganization = () => {
  const authContext = useAuthContext()
  const roleContext = useRoleContext()
  const orgRoles = roleContext && roleContext.orgRoles
  const access = (orgRoles && orgRoles[ROLE_ORG.ORGANIZATION]) || noAccess
  const claims = (authContext.state && authContext.state.jwtClaims) || {}
  const impersonated = !!claims.impersonationEndsAt

  return useMemo(
    () => {
      if (impersonated) {
        const couldWrite = !!(access.accessUpdate || access.accessDelete)
        return { accessRead: !!access.accessRead, accessUpdate: false, accessDelete: false, readOnly: couldWrite ? ORGANIZATION_READ_ONLY.IMPERSONATED : null }
      }
      return { accessRead: !!access.accessRead, accessUpdate: !!access.accessUpdate, accessDelete: !!access.accessDelete, readOnly: null }
    },
    [impersonated, access]
  )
}
